import { useEffect } from 'react';
import { Pagination, Path, StatusLabel } from '@shared/components';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { FoldersNavDrawer, TFolder, TPath } from '@modules/items';
import { AccessType } from '@modules/access/types';
import { useRegistrations } from '../api';
import { REGISTRATIONS_NAV_VALUE, REGISTRATIONS_PATH } from '../constants';
import { useRegistrationsUrlState } from '../hooks/useRegistrationsUrlState';
import RegistrationsFilterBar from './RegistrationsFilterBar';
import RegistrationsScopeSelectors from './RegistrationsScopeSelectors';
import RegistrationExpirationsChart from './RegistrationExpirationsChart';
import RegistrationsTable from './RegistrationsTable';

type TProps = {
  countriesFolder: TFolder;
};

// Country -> products and product -> countries are the same rows: filter by a country to read its products, or by a
// product to read its countries.
const RegistrationsContent = ({ countriesFolder }: TProps) => {
  const { filters, page, pageSize, setFilters, setPage, setPageSize } = useRegistrationsUrlState();
  const { data, isFetching, isPlaceholderData, error } = useRegistrations({ ...filters, page, pageSize });

  // A deep link, or rows moving to other pages, can leave the page past the end: show the last page instead.
  useEffect(() => {
    if (!data || isPlaceholderData || page <= 1 || data.items.length > 0 || data.total === 0) return;
    setPage(Math.ceil(data.total / pageSize), { replace: true });
  }, [data, isPlaceholderData, page, pageSize]);

  const fullPath: TPath = [
    { _id: countriesFolder._id, name: countriesFolder.name, accessRight: countriesFolder.accessRight },
    { _id: REGISTRATIONS_NAV_VALUE, name: 'Dashboard', accessRight: AccessType.NONE, href: REGISTRATIONS_PATH },
  ];

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer folder={countriesFolder} fullPath={fullPath} selectedValue={REGISTRATIONS_NAV_VALUE} />

      <div className="grow h-full w-full min-w-0 relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className="flex flex-col space-y-5 pt-4 pb-16 pl-5 pr-8">
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />

            <RegistrationsScopeSelectors filters={filters} choices={data?.filters} onChange={setFilters} />

            <RegistrationExpirationsChart
              filters={{ countryIds: filters.countryIds, productIds: filters.productIds }}
              onChange={setFilters}
            />

            <div className="flex flex-col gap-3 pt-3">
              <RegistrationsFilterBar filters={filters} onChange={setFilters} />

              <StatusLabel
                data={data}
                error={error}
                loadingMessage={`Loading ${TERMINOLOGY.registrationFull.lower}...`}
                errorMessage={`Loading ${TERMINOLOGY.registrationFull.lower} failed!`}
                render={(loaded) =>
                  loaded.items.length > 0 ? (
                    <div>
                      <RegistrationsTable items={loaded.items} />
                      <Pagination
                        total={loaded.total}
                        page={page}
                        // As in the URL-driven Pagination: "Showing 2 of 2", not "Showing 25 of 2".
                        pageSize={Math.min(pageSize, loaded.total)}
                        isFetching={isFetching}
                        showTotal
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                      />
                    </div>
                  ) : (
                    <p className="text-neutral-500 py-8">No {TERMINOLOGY.registrationFull.lower} found.</p>
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationsContent;
